import { DownOutlined } from '@ant-design/icons';
import { App, Dropdown, Space } from 'antd';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';
import { getLocale } from 'translations/locale';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { useTableParams } from 'business/providers/table-params';
import { formatFilterParams } from 'business/quality-control/pages/segment-history/hooks/data/utils';
import { useSegmentHistoryFilters } from 'business/quality-control/pages/segment-history/providers/filters';
import config from 'config';
import {
  useSegmentHistoryV1ControllerExportSegments,
  useSegmentHistoryV1ControllerExportSegmentsHistory,
} from 'generated/apiComponents';
import { SORT_ORDER } from 'technical/graphql/sort-order';
import { downloadFile } from 'technical/print';
import Button from 'ui/button';

const useSegmentHistoryExport = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const {
    mutate: generateSegmentHistoryExport,
    isPending: isPendingSegmentHistoryExport,
  } = useSegmentHistoryV1ControllerExportSegmentsHistory({
    onSuccess: (data) => {
      downloadFile(`${config.gcp.publicUri}/${data.url}`, data.fileName);
    },
    onError: () => {
      message.open({
        type: 'error',
        content: t('errors.export_download_error'),
      });
    },
  });

  return { generateSegmentHistoryExport, isPendingSegmentHistoryExport };
};

const useSegmentsExport = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const { mutate: generateSegmentsExport, isPending: isPendingSegmentsExport } =
    useSegmentHistoryV1ControllerExportSegments({
      onSuccess: (data) => {
        downloadFile(`${config.gcp.publicUri}/${data.url}`, data.fileName);
      },
      onError: () => {
        message.open({
          type: 'error',
          content: t('errors.export_download_error'),
        });
      },
    });

  return { generateSegmentsExport, isPendingSegmentsExport };
};

export const SegmentHistoryExportButtons = () => {
  const { currentConstructionSiteId } = useAppContext();
  invariant(currentConstructionSiteId, 'No construction site');

  const { t } = useTranslation();
  const filters = useSegmentHistoryFilters();

  const { generateSegmentHistoryExport, isPendingSegmentHistoryExport } =
    useSegmentHistoryExport();
  const { generateSegmentsExport, isPendingSegmentsExport } =
    useSegmentsExport();

  const tableParams = useTableParams();

  const sortField = tableParams.tableParams.sortField;
  const sortOrder = tableParams.tableParams.sortOrder
    ? SORT_ORDER[tableParams.tableParams.sortOrder]
    : null;

  const params = {
    pathParams: {
      constructionSiteId: currentConstructionSiteId,
    },
    body: {
      ...formatFilterParams(filters),
      locale: getLocale(),
      paginationSortField: sortField?.toString(),
      paginationSortDirection: sortOrder,
    },
  };

  const handleClickExportSegments = () => {
    generateSegmentsExport(params);
  };

  const handleClickExportSegmentHistory = () => {
    generateSegmentHistoryExport(params);
  };

  const items = [
    {
      key: 'export-seg',
      label: <div>{t('qualityControl.segmentHistory.cta.exportSegments')}</div>,
      onClick: handleClickExportSegments,
    },
    {
      key: 'export-all',
      label: (
        <div>{t('qualityControl.segmentHistory.cta.exportSegmentHistory')}</div>
      ),
      onClick: handleClickExportSegmentHistory,
    },
  ];

  return (
    <Dropdown menu={{ items }} trigger={['click']}>
      <Button
        type="primary"
        loading={isPendingSegmentHistoryExport || isPendingSegmentsExport}
      >
        <Space>
          {t('common.export')}
          <DownOutlined />
        </Space>
      </Button>
    </Dropdown>
  );
};
